/**
 * LorSelectionStep
 *
 * Wizard step for selecting a Letter of Representation (LoR) from a provided list
 * of document instances. The step writes the selected document id into wizard data as
 * `selected_document_id` and mirrors it in `attachment_document_ids` for downstream
 * processing.
 *
 * Validation: the step is valid when a document is selected.
 *
 * Example:
 * ```tsx
 * <LorSelectionStep
 *   matterId={matterId}
 *   documents={[{ id: 'doc_1', title: 'LoR' }]}
 * />
 * ```
 */
import * as React from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { WizardStep } from './WizardStep';

/**
 * LoR document item.
 */
type LorItem = { id: string; title: string; createdAt?: string };

/**
 * Shared wizard data shape.
 */
interface WizardData {
  selected_document_id?: string;
  attachment_document_ids?: string[];
  [key: string]: unknown;
}

/**
 * Props for LorSelectionStep.
 *
 * - `matterId`: Included for parity with other steps (not used directly).
 * - `documents`: Available LoR documents for selection.
 * - `wizardData`: Current wizard data (used to initialize selection).
 * - `updateStepData`: Callback to persist step data into the wizard store.
 * - `markStepComplete`: Signals validity to the wizard container.
 * - `className`: Optional extra classes for outer container.
 * - `title`: Step title (default: "Select Letter of Representation").
 * - `description`: Step description (default: "Choose the LoR you want to fax").
 */
interface LorSelectionStepProps {
  matterId: string; // parity with other steps
  documents: LorItem[];
  wizardData?: WizardData;
  updateStepData?: (data: Partial<WizardData>) => void;
  markStepComplete?: (isValid: boolean) => void;
  className?: string;
  title?: string;
  description?: string;
}

/**
 * Wizard step component that captures a single LoR selection and updates wizard data.
 */
export const LorSelectionStep: React.FC<LorSelectionStepProps> = ({
  matterId,
  documents,
  wizardData = {},
  updateStepData,
  markStepComplete,
  className = '',
  title = 'Select Letter of Representation',
  description = 'Choose the LoR you want to fax',
}) => {
  const [selectedId, setSelectedId] = React.useState<string>(
    wizardData.selected_document_id || documents[0]?.id || '',
  );

  React.useEffect(() => {
    const isValid = Boolean(selectedId);
    updateStepData?.({
      selected_document_id: selectedId,
      attachment_document_ids: selectedId ? [selectedId] : [],
    });
    markStepComplete?.(isValid);
  }, [selectedId, updateStepData, markStepComplete]);

  return (
    <WizardStep
      title={title}
      description={description}
      isValid={Boolean(selectedId)}
      markStepComplete={markStepComplete}
      className={className}
    >
      <div className="space-y-2" data-matter-id={matterId}>
        <span
          id="lor-selection-label"
          className="text-sm font-medium text-gray-700"
        >
          Letter of Representation
        </span>
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger
            aria-labelledby="lor-selection-label"
            aria-label="Select LoR"
          >
            <SelectValue placeholder="Select a document" />
          </SelectTrigger>
          <SelectContent>
            {documents.map((doc) => {
              const formattedTime = doc.createdAt
                ? new Date(doc.createdAt).toLocaleString()
                : '';
              const optionLabel = formattedTime
                ? `${doc.title} — ${formattedTime}`
                : doc.title;
              return (
                <SelectItem key={doc.id} value={doc.id}>
                  {optionLabel}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </WizardStep>
  );
};
